import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { Check, LucideIcon, PlusCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"

interface FilterOption {
    value: string
    label: string
    icon?: LucideIcon
}

interface FilterSection {
    title: string
    options: FilterOption[]
    selectedValues: Set<string>
    onSelect: (value: string) => void
}

interface CategoryFilterPopoverProps {
    title: string
    sections: FilterSection[]
    onClearAll: () => void
    icon?: LucideIcon
}

export function CategoryFilterPopover({
    title,
    sections,
    onClearAll,
    icon: Icon = PlusCircle
}: CategoryFilterPopoverProps) {
    const totalSelected = sections.reduce((acc, section) => acc + section.selectedValues.size, 0)

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 border-dashed px-4 font-semibold border-muted-foreground/20">
                    <Icon className="mr-2 h-4 w-4" />
                    {title}
                    {totalSelected > 0 && (
                        <>
                            <Separator orientation="vertical" className="mx-2 h-4" />
                            <Badge
                                variant="secondary"
                                className="rounded-sm px-1 font-normal lg:hidden"
                            >
                                {totalSelected}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                                <Badge
                                    variant="secondary"
                                    className="rounded-sm px-1 font-normal"
                                >
                                    {totalSelected} selected
                                </Badge>
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start">
                <Accordion type="single" collapsible defaultValue={sections[0]?.title}>
                    {sections.map((section) => (
                        <AccordionItem value={section.title} key={section.title} className="border-b-0">
                            <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-muted/50 text-sm font-medium">
                                {section.title}
                                {section.selectedValues.size > 0 && (
                                    <Badge variant="secondary" className="ml-auto text-xs h-5 px-1.5 min-w-5 flex justify-center">
                                        {section.selectedValues.size}
                                    </Badge>
                                )}
                            </AccordionTrigger>
                            <AccordionContent className="p-0 border-t">
                                <Command>
                                    <CommandInput placeholder={`Search ${section.title.toLowerCase()}...`} className="h-9" />
                                    <CommandList className="max-h-[180px]">
                                        <CommandEmpty>No results found.</CommandEmpty>
                                        <CommandGroup>
                                            {section.options.map((option) => {
                                                const isSelected = section.selectedValues.has(option.value)
                                                return (
                                                    <CommandItem
                                                        key={option.value}
                                                        onSelect={() => section.onSelect(option.value)}
                                                    >
                                                        <div
                                                            className={cn(
                                                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                                isSelected
                                                                    ? "bg-primary text-primary-foreground"
                                                                    : "opacity-50 [&_svg]:invisible"
                                                            )}
                                                        >
                                                            <Check className={cn("h-4 w-4")} />
                                                        </div>
                                                        {option.icon && (
                                                            <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                                                        )}
                                                        <span>{option.label}</span>
                                                    </CommandItem>
                                                )
                                            })}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                {totalSelected > 0 && (
                    <div className="border-t p-2">
                        <Button
                            variant="ghost"
                            className="w-full justify-center text-sm h-8"
                            onClick={onClearAll}
                        >
                            Clear filters
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    )
}
